import {
  createCheckoutState,
  extractTicketDataFromElement,
  setQuantity,
  syncGroupPersonsFromForm,
  syncPlusOnesFromForm,
} from '../checkout-core/state.js';
import {
  calculatePrice,
  formatCurrency,
  buildDiscountLabel,
  buildTicketLabel,
} from '../checkout-core/pricing.js';
import {
  apiCall,
  endpoints,
} from '../checkout-core/api.js';
import {
  validateField,
  validateBirthDate,
  showFieldError,
  clearFieldError,
  canonicalizeEmail,
} from '../checkout-core/validation.js';
import { escapeHtml } from './sanitize.js';
import { resizeContainer, showToast, showFeedback } from './form-helpers.js';
import { logger } from './logger.js';
import { collectCustomFields, getParamOrHidden } from './utils.js';
import {
  getBotProtectionConfig,
  injectBotProtection,
  resolveBotProtectionResponse,
  refreshBotProtection,
} from './bot-protection.js';
import { safeRedirect } from '../checkout-core/security.js';
import { icon } from '../../shared/icons/render.js';

const __ = (typeof window.__ === 'function')
  ? window.__
  : function(message, replacements) {
    const text = String(message == null ? '' : message);
    if (!Array.isArray(replacements) || replacements.length === 0) return text;
    return text.replace(/\{(\d+)\}/g, (match, idx) => {
      const value = replacements[Number(idx)];
      return value === undefined || value === null ? match : String(value);
    });
  };

const FORM_STATE = new WeakMap();
const PROMO_REVALIDATE_DELAY = 350;
const STRIPE_JS_URL = 'https://js.stripe.com/v3/';

// Module-level wallet availability cache (persists across soft reloads)
const WALLET_CACHE = {};
const WALLET_PENDING = {};
const WALLET_CACHE_TTL = 120_000; // 2 minutes
const WALLET_FAIL_TS = {};
const WALLET_ERROR_COOLDOWN = 10_000; // 10s

// Request queue: throttle concurrent wallet availability checks to avoid HTTP 429
const WALLET_QUEUE_MAX = 3;
let walletQueueActive = 0;
const walletQueuePending = [];

function enqueueWalletRequest(fn) {
  return new Promise((resolve, reject) => {
    const run = async () => {
      walletQueueActive++;
      try { resolve(await fn()); }
      catch (e) { reject(e); }
      finally {
        walletQueueActive--;
        if (walletQueuePending.length > 0) {
          walletQueuePending.shift()();
        }
      }
    };
    if (walletQueueActive < WALLET_QUEUE_MAX) {
      run();
    } else {
      walletQueuePending.push(run);
    }
  });
}

// ---------------------------------------------------------------------------
// Tier Pricing Table (informational display, auto-selected by quantity)
// ---------------------------------------------------------------------------

/**
 * Build the tier pricing info table HTML.
 * Shows volume discount tiers; active tier is highlighted.
 *
 * @param {Object} ctx - Checkout context
 * @returns {string} HTML string or '' if no tiers
 */
function buildTierPricingTable(ctx) {
  const tiers = ctx.ticketData.tiers;
  if (!tiers || tiers.length === 0) return '';

  const currency = ctx.ticketData.currency || 'EUR';
  const basePrice = ctx.ticketData.price;
  const pricing = calculatePrice(ctx.state, ctx.ticketData);
  const currentTierId = pricing.selectedTier;

  const rows = tiers.map((tier, index) => {
    const isActive = tier.name === currentTierId;
    const savings = basePrice > tier.price ? basePrice - tier.price : 0;
    const savingsPercent = basePrice > 0 && savings > 0
      ? Math.round((savings / basePrice) * 100)
      : 0;
    const qtyRange = tier.max_quantity
      ? `${tier.min_quantity || 1}\u2013${tier.max_quantity}`
      : `${tier.min_quantity || 1}+`;
    const activeClass = isActive ? 'tier-row--active' : '';
    const savingsBadge = savingsPercent > 0
      ? `<span class="tier-savings-badge">-${escapeHtml(String(savingsPercent))}%</span>`
      : '';

    return `
      <div class="tier-row ${activeClass}" data-tier-name="${escapeHtml(tier.name)}" data-tier-index="${index}">
        <div class="tier-row-left">
          <span class="tier-row-indicator" aria-hidden="true"></span>
          <span class="tier-row-label">${escapeHtml(tier.label || '')}</span>
          <span class="tier-row-qty">${escapeHtml(qtyRange)} ${__('Stk.')}</span>
        </div>
        <div class="tier-row-right">
          ${savingsBadge}
          <span class="tier-row-price">${escapeHtml(formatCurrency(tier.price, currency))}</span>
        </div>
      </div>`;
  }).join('');

  return `
    <div class="tier-pricing-table" data-tier-table role="region" aria-label="${__('Staffelpreise')}">
      <div class="tier-pricing-header">
        ${icon('trendingUp', { size: 16 })}
        <span class="tier-pricing-title">${__('Staffelpreise')}</span>
      </div>
      <div class="tier-rows">${rows}</div>
      <div class="tier-next-hint" data-tier-hint aria-live="polite"></div>
    </div>`;
}

/**
 * Build "next tier" savings hint text.
 *
 * @param {Object} ctx - Checkout context
 * @returns {string} Hint text or ''
 */
function buildNextTierHint(ctx) {
  const tiers = ctx.ticketData.tiers;
  if (!tiers || tiers.length === 0) return '';

  const quantity = ctx.state.quantity;
  const currency = ctx.ticketData.currency || 'EUR';

  const sortedTiers = [...tiers].sort((a, b) =>
    (a.min_quantity || 1) - (b.min_quantity || 1)
  );
  const nextTier = sortedTiers.find(t => (t.min_quantity || 1) > quantity);
  if (!nextTier) return '';

  const ticketsNeeded = (nextTier.min_quantity || 1) - quantity;
  if (ticketsNeeded <= 0) return '';

  return __('Noch {0} {1} bis \u201E{2}\u201C ({3}/Stk.)', [ticketsNeeded, ticketsNeeded === 1 ? __('Ticket') : __('Tickets'), escapeHtml(nextTier.label || ''), formatCurrency(nextTier.price, currency)]);
}

/**
 * Update tier table to reflect current quantity / active tier.
 *
 * @param {Object} ctx - Checkout context
 */
function updateTierPricingTable(ctx) {
  const table = ctx.elements.tierTable;
  if (!table) return;

  const pricing = calculatePrice(ctx.state, ctx.ticketData);
  const currentTierId = pricing.selectedTier;

  const prevTierId = ctx._previousTierId || null;
  ctx._previousTierId = currentTierId;

  const rows = table.querySelectorAll('.tier-row');
  rows.forEach(row => {
    const tierName = row.dataset.tierName;
    const isActive = tierName === currentTierId;
    const wasActive = row.classList.contains('tier-row--active');

    row.classList.toggle('tier-row--active', isActive);

    if (isActive && !wasActive && prevTierId !== null) {
      row.classList.add('tier-row--just-activated');
      setTimeout(() => {
        if (row.isConnected) row.classList.remove('tier-row--just-activated');
      }, 600);
    }
  });

  const hintEl = table.querySelector('[data-tier-hint]');
  if (hintEl) {
    const hint = buildNextTierHint(ctx);
    if (hint) {
      hintEl.textContent = hint;
      hintEl.classList.remove('hidden');
    } else {
      hintEl.textContent = '';
      hintEl.classList.add('hidden');
    }
  }

  // Announce tier change to screen readers
  if (currentTierId !== prevTierId && prevTierId !== null && pricing.selectedTierLabel) {
    if (ctx._tierAnnouncementTimeout) {
      clearTimeout(ctx._tierAnnouncementTimeout);
    }
    const announcement = __('Preisstufe: {0}, {1} pro Ticket', [pricing.selectedTierLabel, formatCurrency(pricing.unitPrice, ctx.ticketData.currency || 'EUR')]);
    if (hintEl) {
      hintEl.textContent = announcement;
      ctx._tierAnnouncementTimeout = setTimeout(() => {
        const freshHint = buildNextTierHint(ctx);
        hintEl.textContent = freshHint || '';
        if (!freshHint) hintEl.classList.add('hidden');
        ctx._tierAnnouncementTimeout = null;
      }, 3000);
    }
  }
}
let stripeScriptPromise = null;

function ensureStripeJs() {
  if (window.Stripe) {
    return Promise.resolve();
  }
  if (stripeScriptPromise) {
    return stripeScriptPromise;
  }
  stripeScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${STRIPE_JS_URL}"]`);
    if (existing) {
      if (window.Stripe) { resolve(); return; }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('stripe_load_failed')), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = STRIPE_JS_URL;
    script.async = true;
    // CKO-1C: pass per-request CSP nonce so a future nonce-only CSP doesn't
    // block the dynamically injected Stripe loader. Today the CSP also
    // allowlists js.stripe.com explicitly, so this is defense-in-depth.
    const nonceMeta = document.querySelector('meta[name="csp-nonce"]');
    const nonce = nonceMeta?.content || document.currentScript?.nonce;
    if (nonce) script.nonce = nonce;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('stripe_load_failed'));
    document.head.appendChild(script);
  }).catch(err => {
    stripeScriptPromise = null;
    throw err;
  });
  return stripeScriptPromise;
}

function toBool(value) {
  return value === true || value === '1' || value === 'true';
}

function toNumber(value, fallback = 0) {
  const num = parseInt(value, 10);
  if (Number.isNaN(num)) {
    return fallback;
  }
  return num;
}

function getQuantityBounds(ticketData) {
  const minPerOrder = toNumber(ticketData?.minPerOrder, 1) || 1;
  const maxPerOrder = toNumber(ticketData?.maxPerOrder, 10) || 10;

  if (ticketData?.allowGroup) {
    const groupMin = toNumber(ticketData?.groupMin, 1) || 1;
    const groupMax = toNumber(ticketData?.groupMax, 10) || 10;
    // groupMin controls discount threshold only, not minimum order quantity.
    const minQty = Math.max(minPerOrder, 1);
    // BUG-3 FIX: For group bookings, group_max_size is the effective max.
    // Backend uses only group_max_size for groups (stripe_checkout.py:1507-1509).
    // Hard cap at GROUP_MAX_HARD_CAP = 10.
    const maxQty = Math.min(groupMax, 10);
    return {
      minQty,
      maxQty: Math.max(maxQty, minQty),
    };
  }

  return {
    minQty: Math.max(minPerOrder, 1),
    maxQty: Math.max(maxPerOrder, 1),
  };
}

function clampQuantity(value, minQty, maxQty) {
  const safeValue = Number.isFinite(value) ? value : minQty;
  return Math.max(minQty, Math.min(maxQty, safeValue));
}

function getTicketName(form, ticketData) {
  const fromDataset = form?.dataset?.ticketName || form?.dataset?.ticketLabel || '';
  return fromDataset || ticketData?.ticketName || form?.dataset?.ticket || 'Ticket';
}

function hasPromoCodes(form) {
  return toBool(form?.dataset?.hasPromoCodes);
}

function resolveAgeMode(ticketData) {
  return (ticketData?.ageVerificationMode || 'birth_date').trim();
}

function shouldShowBirthDate(ticketData) {
  return (ticketData?.minAge || 0) > 0 && resolveAgeMode(ticketData) === 'birth_date';
}

function shouldShowAgeCheckbox(ticketData) {
  return (ticketData?.minAge || 0) > 0 && resolveAgeMode(ticketData) === 'checkbox';
}

function attachDynamicValidation(input, options) {
  if (!input || input.dataset.validationInit === '1') {
    return;
  }
  input.dataset.validationInit = '1';

  const validate = () => {
    const result = validateField(input, options);
    if (!result.valid) {
      showFieldError(input, result.message);
      return false;
    }
    clearFieldError(input);
    return true;
  };

  input.addEventListener('blur', validate);
  input.addEventListener('input', () => {
    if (input.getAttribute('aria-invalid') === 'true') {
      validate();
    }
  });

  if (input.type === 'checkbox') {
    input.addEventListener('change', validate);
  }
}

function buildSectionHeader(title, iconSvg) {
  const safeTitle = escapeHtml(title);
  const iconMarkup = iconSvg || '';
  return `
    <div class="checkout-section-icon-header">
      <span class="checkout-section-icon">${iconMarkup}</span>
      <span class="checkout-section-icon-title">${safeTitle}</span>
    </div>
  `;
}

function buildInfoBox(message) {
  return `
    <div class="checkout-info-box">
      ${escapeHtml(message)}
    </div>
  `;
}

function buildQuantityControls(ctx) {
  const { minQty, maxQty, state } = ctx;
  return `
    <div class="flex items-center gap-2">
      <button type="button" data-qty-decrease
        aria-label="${__('Anzahl verringern')}"
        class="checkout-qty-btn checkout-qty-btn--press">-</button>
      <input type="number" inputmode="numeric" name="quantity" min="${minQty}" max="${maxQty}" value="${state.quantity}"
        aria-label="${__('Ticketanzahl')}"
        class="checkout-input w-20 text-center" data-qty-input>
      <button type="button" data-qty-increase
        aria-label="${__('Anzahl erhöhen')}"
        class="checkout-qty-btn checkout-qty-btn--press">+</button>
      <span class="text-xs checkout-helper-text">Min ${minQty}, Max ${maxQty}</span>
    </div>
    <div class="checkout-sr-only" aria-live="assertive" aria-atomic="true" data-qty-announce></div>
  `;
}

function buildGroupSection(ctx) {
  const ticket = ctx.ticketData;

  const discountNote = ticket.groupDiscountType
    ? __('Gruppenrabatt ab {0} Personen: {1}', [ticket.groupMin || 2, ticket.groupDiscountType === 'Prozent'
        ? `${ticket.groupDiscountValue || 0}%`
        : `${formatCurrency(ticket.groupDiscountValue || 0, ticket.currency || 'EUR')} ${__('pro Person')}`])
    : '';

  const iconMarkup = icon('users');

  return `
    <fieldset class="checkout-section" data-checkout-group>
      ${buildSectionHeader(__('Gruppenbuchung'), iconMarkup)}
      <div class="space-y-2">
        ${buildQuantityControls(ctx)}
        ${buildTierPricingTable(ctx)}
        ${discountNote ? `<div class="text-xs checkout-helper-text">${escapeHtml(discountNote)}</div>` : ''}
      </div>
      <div class="mt-4 space-y-3" data-group-persons></div>
    </fieldset>
  `;
}

function buildPlusOneSection(ctx) {
  const icon = `
    <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"/>
    </svg>
  `;

  return `
    <fieldset class="checkout-section" data-checkout-plus-one>
      ${buildSectionHeader(__('Eine Begleitperson (gratis)'), icon)}
      <div class="space-y-3" data-plus-one-persons></div>
      <button type="button" class="mt-2 checkout-btn-secondary" data-plus-one-add>
        ${__('Begleitperson hinzufügen')}
      </button>
      <div class="hidden text-xs checkout-text-warning mt-2" data-plus-one-warning></div>
    </fieldset>
  `;
}

function buildPromoSection() {
  const iconMarkup = icon('tag');

  return `
    <fieldset class="checkout-section" data-checkout-promo>
      ${buildSectionHeader(__('Rabattcode'), iconMarkup)}
      <div class="flex flex-wrap gap-2 items-center">
        <input type="text" name="promo_code_input" class="checkout-input flex-1 min-w-[120px]" placeholder="${__('Code eingeben')}" data-promo-input aria-label="${__('Rabattcode eingeben')}">
        <button type="button" class="min-h-[44px] checkout-btn-secondary" data-promo-apply aria-label="${__('Rabattcode einlösen')}">${__('Einlösen')}</button>
      </div>
      <div class="mt-2 text-xs hidden" data-promo-feedback role="status" aria-live="polite"></div>
    </fieldset>
  `;
}

function buildWalletSection() {
  const icon = `
    <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" d="M12 3v18m9-9H3"/>
    </svg>
  `;

  return `
    <fieldset class="checkout-section hidden" data-checkout-wallet>
      ${buildSectionHeader(__('Wallet Payment'), icon)}
      <div class="checkout-wallet-container">
        <button type="button" class="hidden checkout-wallet-btn" data-wallet-apple aria-label="${__('Mit Apple Pay bezahlen')}">
          Apple Pay
        </button>
        <button type="button" class="hidden checkout-wallet-btn" data-wallet-google aria-label="${__('Mit Google Pay bezahlen')}">
          Google Pay
        </button>
        <div class="checkout-helper-text checkout-wallet-hint" data-wallet-hint role="status" aria-live="polite">${__('Wallets werden geladen …')}</div>
      </div>
    </fieldset>
  `;
}

function buildSummarySection() {
  return `
    <div class="checkout-summary-box" data-checkout-summary aria-live="polite" aria-atomic="true"></div>
  `;
}

/**
 * Sticky summary bar — appears when the inline summary scrolls out of viewport.
 * Initialized once per form, updated on every refreshSummary call.
 */
function initStickySummary(ctx) {
  if (ctx._stickySummaryInit) return;
  ctx._stickySummaryInit = true;

  const summaryEl = ctx.elements.summary;
  if (!summaryEl) return;

  // Create the sticky bar (appended to body so it sits outside scroll containers)
  const bar = document.createElement('div');
  bar.className = 'checkout-summary-sticky';
  bar.setAttribute('aria-hidden', 'true');
  bar.innerHTML = `
    <div>
      <div class="checkout-summary-sticky-label" data-sticky-label></div>
      <div class="checkout-summary-sticky-total" data-sticky-total></div>
    </div>
    <button type="button" class="checkout-summary-sticky-btn" data-sticky-btn>${__('Jetzt bezahlen')}</button>
  `;
  document.body.appendChild(bar);
  ctx._stickyBar = bar;

  // Click on sticky button scrolls to and clicks the real submit button
  bar.querySelector('[data-sticky-btn]').addEventListener('click', () => {
    const submitBtn = ctx.form.querySelector('button[type="submit"]');
    if (!submitBtn) return;

    submitBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Use scrollend event with safety fallback to avoid race condition
    let fired = false;
    const doClick = () => {
      if (fired) return;
      fired = true;
      submitBtn.click();
    };
    if ('onscrollend' in window) {
      window.addEventListener('scrollend', doClick, { once: true });
      setTimeout(() => { window.removeEventListener('scrollend', doClick); doClick(); }, 800);
    } else {
      setTimeout(doClick, 600);
    }
  });

  // IntersectionObserver: show sticky bar when summary is not visible
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const formExpanded = ctx.form && ctx.form.dataset.expanded === '1';
        if (!entry.isIntersecting && formExpanded) {
          bar.classList.add('is-visible');
        } else {
          bar.classList.remove('is-visible');
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(summaryEl);
    ctx._stickyObserver = observer;
  }
}

/**
 * Update the sticky bar content with current pricing data.
 */
function updateStickySummary(ctx, pricing) {
  const bar = ctx._stickyBar;
  if (!bar) return;

  const { ticketData } = ctx;
  const isFree = ticketData.isFree || (pricing && pricing.total === 0);

  const labelEl = bar.querySelector('[data-sticky-label]');
  const totalEl = bar.querySelector('[data-sticky-total]');
  const btnEl = bar.querySelector('[data-sticky-btn]');

  if (labelEl) labelEl.textContent = __('Gesamt');
  if (totalEl) totalEl.textContent = isFree ? __('Kostenlos') : (pricing ? formatCurrency(pricing.total, pricing.currency) : '');

  if (btnEl) {
    if (ticketData.requiresApproval) {
      btnEl.textContent = __('Anfrage senden');
    } else if (isFree) {
      btnEl.textContent = __('Kostenlos registrieren');
    } else {
      btnEl.textContent = __('Jetzt bezahlen');
    }
  }
}

/**
 * Clean up sticky summary bar: remove DOM element and disconnect observer.
 * Called when form is collapsed to prevent memory leaks.
 */
export function cleanupStickySummary(ctx) {
  if (ctx._stickyBar) {
    ctx._stickyBar.remove();
    ctx._stickyBar = null;
  }
  if (ctx._stickyObserver) {
    ctx._stickyObserver.disconnect();
    ctx._stickyObserver = null;
  }
  ctx._stickySummaryInit = false;
}

/**
 * Build the capacity bar showing remaining tickets with "Fast weg!" alert.
 */
function buildCapacityBar(ticketData, config) {
  const remainingCount = ticketData.remainingCount;
  const maxQuantity = ticketData.maxQuantity;
  const showRemaining = config?.showRemaining;
  if (!showRemaining || remainingCount === null || remainingCount === undefined || ticketData.soldOut) {
    return '';
  }
  const isCritical = remainingCount < 5;
  // Bounds check: clamp fillPercent between 0-100% to handle data anomalies
  const rawPercent = maxQuantity && maxQuantity > 0
    ? ((maxQuantity - remainingCount) / maxQuantity) * 100
    : 0;
  const fillPercent = Math.round(Math.max(0, Math.min(100, rawPercent)));
  const warningIcon = `${icon('exclamation', { size: 12 })}`;
  return `
    <div class="checkout-capacity${isCritical ? ' checkout-capacity--critical' : ''}" data-checkout-capacity>
      <div class="checkout-capacity-bar">
        <div class="checkout-capacity-fill" style="width: ${fillPercent}%"></div>
      </div>
      <span class="checkout-capacity-text">
        ${__('Noch')} <strong>${remainingCount}</strong>${maxQuantity ? ` ${__('von')} ${maxQuantity}` : ''} ${__('verfügbar')}
        ${isCritical ? `<span class="checkout-capacity-alert">${warningIcon} ${__('Fast weg!')}</span>` : ''}
      </span>
    </div>
  `;
}

/**
 * Build trust signals footer with security badges.
 */
function buildTrustSignals(isPaid) {
  return `
    <div class="checkout-trust" data-checkout-trust>
      <span class="checkout-trust-item">
        ${icon('lock', { size: 16 })}
        ${isPaid ? __('Sichere Zahlung') : __('Sichere Datenverarbeitung')}
      </span>
      <span class="checkout-trust-item">
        ${icon('mail', { size: 16 })}
        ${isPaid ? __('Sofort-Versand') : __('Sofortige Bestätigung')}
      </span>
      <span class="checkout-trust-item">
        ${icon('shieldCheck', { size: 16 })}
        ${__('SSL-verschlüsselt')}
      </span>
    </div>
  `;
}

/**
 * Update the submit button text based on ticket type and current pricing.
 * Called on init and after every price recalculation (quantity, promo, group changes).
 */
function updateSubmitButtonText(form, ticketData, pricing) {
  const submitBtn = form.querySelector('button[type="submit"]');
  if (!submitBtn) return;

  const textEl = submitBtn.querySelector('.checkout-btn-text, .btn-text');
  if (!textEl) return;

  let newText;
  if (ticketData.requiresApproval) {
    newText = __('Anfrage senden');
  } else if (ticketData.isFree || (pricing && pricing.total === 0)) {
    newText = __('Kostenlos registrieren');
  } else if (ticketData.isPaid && pricing && pricing.total > 0) {
    newText = __('Jetzt bezahlen') + ' (' + formatCurrency(pricing.total, pricing.currency) + ')';
  } else if (ticketData.isPaid) {
    newText = __('Jetzt bezahlen');
  } else {
    newText = __('Jetzt anmelden');
  }

  textEl.textContent = newText;
}

function buildPersonCard({ title, index, displayIndex, namePrefix, person, showBirthDate, showAgeCheckbox, minAge, type, customFieldDefs, showMembership, membershipRequired, membershipLabel }) {
  const safeIndex = escapeHtml(displayIndex);
  const safeTitle = escapeHtml(title);
  const firstName = escapeHtml(person.first_name || '');
  const lastName = escapeHtml(person.last_name || '');
  const email = escapeHtml(person.email || '');
  const birthDate = escapeHtml(person.birth_date || '');

  // M6 FIX: Generate unique IDs for label/input association (WCAG 1.3.1 / 4.1.2)
  const fnId = `${namePrefix}first_name_${index}`;
  const lnId = `${namePrefix}last_name_${index}`;
  const emId = `${namePrefix}email_${index}`;
  const bdId = `${namePrefix}birth_date_${index}`;

  // Group person cards are always additional persons (Person 2+).
  // The main booker (Person 1) fills out the main form fields directly.
  const subtitleText = '';

  return `
    <div class="checkout-person-card space-y-3" data-${type}-person data-index="${index}">
      <div class="checkout-person-card-header">
        <span class="checkout-person-badge">${safeIndex}</span>
        <span class="checkout-person-title">${safeTitle} ${safeIndex}</span>
        ${subtitleText}
      </div>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label class="checkout-label" for="${fnId}">${__('Vorname')} <span class="checkout-required">*</span></label>
          <input type="text" id="${fnId}" name="${fnId}" class="checkout-input" value="${firstName}" required>
        </div>
        <div>
          <label class="checkout-label" for="${lnId}">${__('Nachname')} <span class="checkout-required">*</span></label>
          <input type="text" id="${lnId}" name="${lnId}" class="checkout-input" value="${lastName}" required>
        </div>
      </div>
      <div>
        <label class="checkout-label" for="${emId}">${__('E-Mail')} <span class="checkout-required">*</span></label>
        <input type="email" id="${emId}" name="${emId}" class="checkout-input" value="${email}" required>
      </div>
      ${showBirthDate ? `
        <div>
          <label class="checkout-label" for="${bdId}">${__('Geburtsdatum')} <span class="checkout-required">*</span></label>
          <input type="date" id="${bdId}" name="${bdId}" class="checkout-input" min="1900-01-01" value="${birthDate}" required>
        </div>
      ` : ''}
      ${showAgeCheckbox ? `
        <label class="checkout-consent checkout-consent--age">
          <span class="checkout-consent-checkbox">
            <input type="checkbox" name="${namePrefix}age_confirmed_${index}" required ${person.age_confirmed ? 'checked' : ''}>
            <span class="checkout-consent-checkbox-visual">
              ${icon('check')}
            </span>
          </span>
          <span class="checkout-consent-label">${__('Ich bestätige, dass diese Person mindestens {0} Jahre alt ist', [minAge])} <span class="checkout-required">*</span></span>
        </label>
      ` : ''}
      ${showMembership ? `
        <div>
          <label class="checkout-label">${escapeHtml(membershipLabel || __('Zusatzangabe'))}${membershipRequired ? ' <span class="checkout-required">*</span>' : ''}</label>
          <input type="text" name="${namePrefix}membership_number_${index}" class="checkout-input"
                 inputmode="text" autocomplete="off"
                 ${membershipRequired ? 'required' : ''}
                 value="${escapeHtml(person.membership_number || '')}">
        </div>
      ` : ''}
      ${buildPersonCustomFields(customFieldDefs, namePrefix, index, person)}
    </div>
  `;
}

/**
 * Build custom field HTML inputs for a person card.
 *
 * @param {Array} defs - Custom field definitions
 * @param {string} prefix - Name prefix ('group_' or 'plus_one_')
 * @param {number} index - Person index
 * @param {Object} person - Person data (may contain custom_fields)
 * @returns {string} HTML string
 */
function buildPersonCustomFields(defs, prefix, index, person) {
  if (!defs || !defs.length) return '';
  const personCf = (person && person.custom_fields) || {};

  return defs.map(field => {
    const key = field.name || field.field_label || field.label || '';
    if (!key) return '';
    const safeKey = key.replace(/[^a-zA-Z0-9_-]/g, '_');
    const label = escapeHtml(field.label || field.field_label || key);
    const fieldType = (field.type || field.field_type || 'text').toLowerCase();
    const isReq = field.required || field.is_required;
    const reqStar = isReq ? ' <span class="checkout-required">*</span>' : '';
    const reqAttr = isReq ? 'required' : '';
    const tooltip = field.tooltip
      ? `<p class="mt-1 text-xs checkout-helper-text">${escapeHtml(field.tooltip)}</p>`
      : '';
    const existingVal = escapeHtml(personCf[key] != null ? String(personCf[key]) : '');
    const inputName = `${prefix}cf_${safeKey}_${index}`;

    if (fieldType === 'textarea') {
      return `<div>
        <label class="checkout-label">${label}${reqStar}</label>
        <textarea name="${inputName}" data-person-cf="${safeKey}" class="checkout-input" rows="2" placeholder="${escapeHtml(field.placeholder || '')}" ${reqAttr}>${existingVal}</textarea>
        ${tooltip}
      </div>`;
    }
    if (fieldType === 'select') {
      const options = (field.options || []).map(o => {
        const safeOpt = escapeHtml(o);
        return `<option value="${safeOpt}" ${o === (personCf[key] || '') ? 'selected' : ''}>${safeOpt}</option>`;
      }).join('');
      return `<div>
        <label class="checkout-label">${label}${reqStar}</label>
        <select name="${inputName}" data-person-cf="${safeKey}" class="checkout-input" ${reqAttr}>
          <option value="">${__('Bitte wählen')}</option>${options}
        </select>
        ${tooltip}
      </div>`;
    }
    if (fieldType === 'checkbox') {
      const isChecked = personCf[key] === true || personCf[key] === '1' || personCf[key] === 'true';
      return `<label class="checkout-consent">
        <span class="checkout-consent-checkbox">
          <input type="checkbox" name="${inputName}" data-person-cf="${safeKey}"
                 ${reqAttr} ${isChecked ? 'checked' : ''}>
          <span class="checkout-consent-checkbox-visual">
            ${icon('check')}
          </span>
        </span>
        <span class="checkout-consent-label">${label}${reqStar}</span>
      </label>`;
    }
    // text, number, date
    const inputType = fieldType === 'number' ? 'number' : fieldType === 'date' ? 'date' : 'text';
    return `<div>
      <label class="checkout-label">${label}${reqStar}</label>
      <input type="${inputType}" name="${inputName}" data-person-cf="${safeKey}"
             class="checkout-input" value="${existingVal}" placeholder="${escapeHtml(field.placeholder || '')}"
             ${fieldType === 'number' ? 'inputmode="numeric"' : ''} ${reqAttr}>
      ${tooltip}
    </div>`;
  }).join('');
}

function renderGroupPersons(ctx) {
  const { form, state, ticketData } = ctx;
  const container = ctx.elements.groupPersons;
  if (!container) {
    return;
  }

  const showBirthDate = shouldShowBirthDate(ticketData);
  const showAgeCheckbox = shouldShowAgeCheckbox(ticketData);
  const minAge = ticketData.minAge || 0;
  const customFieldDefs = (ticketData.customFieldDefs || []).filter(f => {
    const scope = f.applies_to || 'Alle';
    return scope === 'Alle' || scope === 'Gruppenmitglieder';
  });
  const showMembership = form.dataset.askMembership === '1';
  const membershipRequired = form.dataset.membershipRequired === '1' || form.dataset.membershipRequired === 'true';
  const membershipLabel = form.dataset.membershipLabel || '';

  const useCollapse = state.groupPersons.length > 3;

  container.innerHTML = state.groupPersons
    .map((person, index) => {
      const cardHtml = buildPersonCard({
        title: __('Person'),
        index,
        displayIndex: index + 2,
        namePrefix: 'group_',
        person,
        showBirthDate,
        showAgeCheckbox,
        minAge,
        type: 'group',
        customFieldDefs,
        showMembership,
        membershipRequired,
        membershipLabel,
      });

      // First person always expanded, rest collapsed when many persons
      if (!useCollapse || index === 0) return cardHtml;

      const isComplete = !!(person.first_name && person.last_name && person.email);
      const statusClass = isComplete ? 'checkout-person-status--complete' : 'checkout-person-status--incomplete';
      const statusText = isComplete ? __('vollständig') : __('ausfüllen');
      const safeDisplay = escapeHtml(index + 2);

      return `
        <details class="checkout-person-details">
          <summary>
            <span class="checkout-person-badge">${safeDisplay}</span>
            <span class="checkout-person-title">${__('Person')} ${safeDisplay}</span>
            <span class="checkout-person-status ${statusClass}" role="status" aria-live="polite">${statusText}</span>
          </summary>
          ${cardHtml}
        </details>
      `;
    })
    .join('');

  // Live-update completion status on input blur
  if (useCollapse) {
    container.querySelectorAll('details.checkout-person-details').forEach((details) => {
      details.addEventListener('focusout', () => {
        const inputs = details.querySelectorAll('input[required]');
        const allFilled = Array.from(inputs).every(i => i.type === 'checkbox' ? i.checked : i.value.trim());
        const statusEl = details.querySelector('.checkout-person-status');
        if (statusEl) {
          statusEl.className = 'checkout-person-status ' + (allFilled ? 'checkout-person-status--complete' : 'checkout-person-status--incomplete');
          statusEl.textContent = allFilled ? __('vollständig') : __('ausfüllen');
        }
      });
    });
  }

  const options = { minAge: minAge ?? 0, ageVerificationMode: resolveAgeMode(ticketData) };
  container.querySelectorAll('input, select, textarea').forEach((input) => attachDynamicValidation(input, options));
  resizeContainer(form);
}

function renderPlusOnes(ctx) {
  const { form, state, ticketData } = ctx;
  const container = ctx.elements.plusOnePersons;
  if (!container) {
    return;
  }

  // NOTE: Do NOT call syncPlusOnesFromForm here!
  // The caller (click handler, remove handler) must sync BEFORE modifying state.plusOnes.
  // Syncing here would overwrite changes with empty form data.

  const showBirthDate = shouldShowBirthDate(ticketData);
  const showAgeCheckbox = shouldShowAgeCheckbox(ticketData);
  const minAge = ticketData.minAge || 0;
  const customFieldDefs = (ticketData.customFieldDefs || []).filter(f => {
    const scope = f.applies_to || 'Alle';
    return scope === 'Alle' || scope === 'Plus-Ones';
  });
  const showMembership = form.dataset.askMembership === '1';
  const membershipRequired = form.dataset.membershipRequired === '1' || form.dataset.membershipRequired === 'true';
  const membershipLabel = form.dataset.membershipLabel || '';

  container.innerHTML = state.plusOnes
    .map((person, index) => {
      return `
        <div data-plus-one-card>
          <div class="flex justify-end mb-1">
            <button type="button" class="checkout-helper-text" data-plus-one-remove="${index}" aria-label="${__('Begleitperson {0} entfernen', [index + 1])}">${__('Entfernen')}</button>
          </div>
          ${buildPersonCard({
            title: __('Begleitperson'),
            index,
            displayIndex: index + 1,
            namePrefix: 'plus_one_',
            person,
            showBirthDate,
            showAgeCheckbox,
            minAge,
            type: 'plus-one',
            customFieldDefs,
            showMembership,
            membershipRequired,
            membershipLabel,
          })}
        </div>
      `;
    })
    .join('');

  const options = { minAge: minAge ?? 0, ageVerificationMode: resolveAgeMode(ticketData) };
  container.querySelectorAll('input, select, textarea').forEach((input) => attachDynamicValidation(input, options));

  container.querySelectorAll('[data-plus-one-remove]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const idx = parseInt(btn.dataset.plusOneRemove, 10);
      if (Number.isNaN(idx)) {
        return;
      }
      syncPlusOnesFromForm(form, state);
      // C2 Fix: Bounds check to prevent race condition
      if (idx >= 0 && idx < state.plusOnes.length) {
        state.plusOnes.splice(idx, 1);
      }
      renderPlusOnes(ctx);
      updatePlusOneControls(ctx);
      refreshSummary(ctx);
      updateTierPricingTable(ctx);
    });
  });

  resizeContainer(form);
}

function updateQuantityControls(ctx) {
  const { minQty, maxQty, state } = ctx;
  if (ctx.elements.qtyInput) {
    ctx.elements.qtyInput.value = String(state.quantity);
    // Trigger pulse animation on quantity display
    ctx.elements.qtyInput.classList.remove('checkout-qty-pulse');
    // Force reflow to restart animation — batched in rAF to avoid layout thrashing
    requestAnimationFrame(() => {
      void ctx.elements.qtyInput.offsetWidth;
      ctx.elements.qtyInput.classList.add('checkout-qty-pulse');
    });
  }
  if (ctx.elements.qtyDecrease) {
    ctx.elements.qtyDecrease.disabled = state.quantity <= minQty;
    ctx.elements.qtyDecrease.setAttribute('aria-disabled', state.quantity <= minQty ? 'true' : 'false');
  }
  if (ctx.elements.qtyIncrease) {
    ctx.elements.qtyIncrease.disabled = state.quantity >= maxQty;
    ctx.elements.qtyIncrease.setAttribute('aria-disabled', state.quantity >= maxQty ? 'true' : 'false');
  }

  // Announce quantity change to screen readers
  const announceEl = ctx.elements.container?.querySelector('[data-qty-announce]');
  if (announceEl) {
    announceEl.textContent = __('Anzahl: {0}', [state.quantity]);
  }
}

function updatePlusOneControls(ctx) {
  const { ticketData, state } = ctx;
  if (!ctx.elements.plusOneAdd) {
    return;
  }

  const maxPlus = ticketData.plusOneMax || 1;
  ctx.elements.plusOneAdd.disabled = state.plusOnes.length >= maxPlus;
}

function updatePlusOneAvailability(ctx) {
  if (!ctx.elements.plusOneSection) {
    return;
  }
  const warning = ctx.elements.plusOneWarning;
  if (warning) {
    warning.textContent = '';
    warning.classList.add('hidden');
  }
  updatePlusOneControls(ctx);
}

function getFeedbackBox(form) {
  return form.querySelector('.feedback-box') || form.querySelector('.feedback') || form;
}

function getWalletEligibility(ctx) {
  // WELLE 1 / P1-1: wallet UI is OFF unless the `enable_wallet_payments`
  // setting is on (mirrored into ticketData.walletEnabled). Fail-closed:
  // a missing/false flag keeps Apple/Google Pay fully suppressed.
  return !!(
    ctx.ticketData?.walletEnabled
    && ctx.ticketData?.isPaid
    && !ctx.ticketData?.requiresApproval
    && ctx.config?.eventName
  );
}

function getWalletAmountCents(ctx) {
  const pricing = calculatePrice(ctx.state, ctx.ticketData);
  return Math.max(0, Math.round((pricing.total || 0) * 100));
}

function getWalletAvailabilityCacheKey(eventName, ticketId, inviteToken, inviteCode, accessCode, quantity, groupSize) {
  return [
    eventName || '',
    ticketId || '',
    inviteToken || '',
    inviteCode || '',
    accessCode || '',
    quantity || 1,
    groupSize || 0,
  ].join('::');
}

async function getCachedWalletAvailability(eventName, ticketId, { inviteToken, inviteCode, accessCode, quantity, groupPersons } = {}) {
  const key = getWalletAvailabilityCacheKey(
    eventName,
    ticketId,
    inviteToken,
    inviteCode,
    accessCode,
    quantity,
    groupPersons?.length || 0,
  );
  const cached = WALLET_CACHE[key];
  if (cached && Date.now() - cached.ts < WALLET_CACHE_TTL) {
    return cached.data;
  }
  if (WALLET_PENDING[key]) {
    return WALLET_PENDING[key];
  }
  const lastFail = WALLET_FAIL_TS[key];
  if (lastFail && Date.now() - lastFail < WALLET_ERROR_COOLDOWN) {
    return { success: false, data: { available: false } };
  }
  WALLET_PENDING[key] = enqueueWalletRequest(() => apiCall(endpoints.getWalletAvailability, {
    event: eventName,
    ticket_type: ticketId,
    invite_token: inviteToken || undefined,
    invite_code: inviteCode || undefined,
    access_code: accessCode || undefined,
    quantity: quantity || undefined,
    group_persons: groupPersons?.length ? JSON.stringify(groupPersons) : undefined,
  }, { method: 'GET' })).then((result) => {
    if (result?.success) {
      WALLET_CACHE[key] = { data: result, ts: Date.now() };
      delete WALLET_FAIL_TS[key];
    } else {
      WALLET_FAIL_TS[key] = Date.now();
    }
    delete WALLET_PENDING[key];
    return result;
  }).catch((err) => {
    WALLET_FAIL_TS[key] = Date.now();
    delete WALLET_PENDING[key];
    return { success: false, data: { available: false } };
  });
  return WALLET_PENDING[key];
}

async function initWalletPayments(ctx) {
  if (!getWalletEligibility(ctx) || ctx.wallet?.initialized) {
    return;
  }
  ctx.wallet = ctx.wallet || {};
  ctx.wallet.initialized = true;

  if (!ctx.elements.walletSection) {
    return;
  }

  const amountCents = getWalletAmountCents(ctx);
  if (amountCents <= 0) {
    ctx.elements.walletSection.classList.add('hidden');
    return;
  }

  const inviteToken = getParamOrHidden(ctx.form, ['invite', 'invite_token']);
  const inviteCode = getParamOrHidden(ctx.form, 'invite_code');
  const accessCode = getParamOrHidden(ctx.form, 'access_code');
  const availability = await getCachedWalletAvailability(
    ctx.config?.eventName,
    ctx.ticketData.ticketId,
    {
      inviteToken,
      inviteCode,
      accessCode,
      quantity: ctx.state.quantity,
      groupPersons: ctx.state.groupPersons,
    },
  );

  if (!availability?.success || !availability?.data?.available) {
    ctx.elements.walletSection.classList.add('hidden');
    return;
  }

  try {
    await ensureStripeJs();
  } catch (error) {
    ctx.elements.walletSection.classList.add('hidden');
    return;
  }

  const stripeAccount = availability.data.connected_account_id;
  const publishableKey = availability.data.publishable_key;
  if (!window.Stripe || !publishableKey || !stripeAccount) {
    ctx.elements.walletSection.classList.add('hidden');
    return;
  }

  ctx.wallet.stripe = window.Stripe(publishableKey, { stripeAccount });

  const paymentRequest = ctx.wallet.stripe.paymentRequest({
    country: availability.data.country || 'DE',
    currency: (ctx.ticketData.currency || availability.data.currency || 'eur').toLowerCase(),
    total: {
      label: ctx.ticketData.ticketName || 'Ticket',
      amount: amountCents,
    },
    requestPayerName: true,
    requestPayerEmail: true,
  });

  const canMakePayment = await paymentRequest.canMakePayment();
  logger.info?.('Wallet eligibility', {
    flow: ctx.state.groupPersons?.length ? 'group' : 'single',
    quantity: ctx.state.quantity,
    canMakePayment,
    diagnostics: availability.data.wallet_diagnostics || null,
  });
  if (!canMakePayment) {
    ctx.elements.walletSection.classList.add('hidden');
    return;
  }

  ctx.wallet.paymentRequest = paymentRequest;
  ctx.wallet.canMakePayment = canMakePayment;

  if (ctx.elements.walletApple) {
    ctx.elements.walletApple.classList.toggle('hidden', !canMakePayment.applePay);
  }
  if (ctx.elements.walletGoogle) {
    ctx.elements.walletGoogle.classList.toggle('hidden', !canMakePayment.googlePay);
  }

  if (!canMakePayment.applePay && !canMakePayment.googlePay) {
    ctx.elements.walletSection.classList.add('hidden');
    return;
  }

  ctx.elements.walletSection.classList.remove('hidden');
  if (ctx.elements.walletHint) {
    ctx.elements.walletHint.textContent = __('Schnell bezahlen mit Apple Pay oder Google Pay.');
  }

  paymentRequest.on('paymentmethod', async (ev) => {
    await handleWalletPaymentMethod(ctx, ev);
  });

  if (ctx.elements.walletApple) {
    ctx.elements.walletApple.addEventListener('click', () => handleWalletPaymentStart(ctx));
  }
  if (ctx.elements.walletGoogle) {
    ctx.elements.walletGoogle.addEventListener('click', () => handleWalletPaymentStart(ctx));
  }
}

/**
 * Reset wallet busy flag with debounce to prevent race conditions.
 * H4 Fix: Adds minimum delay before allowing new payment attempts.
 * @param {Object} ctx - Checkout context
 * @param {number} delay - Delay in ms (default 300ms for errors, 0 for success)
 */
function resetWalletBusy(ctx, delay = 300) {
  if (!ctx.wallet) return;
  if (delay > 0) {
    setTimeout(() => {
      ctx.wallet.busy = false;
    }, delay);
  } else {
    ctx.wallet.busy = false;
  }
}

function updateWalletPricing(ctx) {
  if (!ctx.wallet?.paymentRequest) {
    return;
  }
  const amountCents = getWalletAmountCents(ctx);
  if (amountCents <= 0) {
    ctx.elements.walletSection?.classList.add('hidden');
    return;
  }
  ctx.wallet.paymentRequest.update({
    total: {
      label: ctx.ticketData.ticketName || 'Ticket',
      amount: amountCents,
    },
  });
}

function validateWalletForm(ctx) {
  const form = ctx.form;
  const feedback = getFeedbackBox(form);
  const showError = (msg) => {
    showFeedback(feedback, msg, 'error');
    showToast(msg, 'error');
  };

  const consentDsgvo = form.querySelector('input[name="consent_dsgvo"]');
  const consentAgb = form.querySelector('input[name="consent_agb"]');
  const consentOrganizer = form.querySelector('input[name="consent_organizer_terms"]');

  if (consentDsgvo && !consentDsgvo.checked) {
    showError(__('Bitte stimme der Datenschutzerklärung zu.'));
    return false;
  }
  if (consentAgb && !consentAgb.checked) {
    showError(__('Bitte stimme den AGB zu.'));
    return false;
  }
  if (consentOrganizer && consentOrganizer.hasAttribute('required') && !consentOrganizer.checked) {
    showError(__('Bitte stimme den Veranstalter-Bedingungen zu.'));
    return false;
  }

  const minAge = ctx.ticketData?.minAge || 0;
  const ageMode = resolveAgeMode(ctx.ticketData);
  if (minAge > 0) {
    if (ageMode === 'checkbox') {
      const ageCheckbox = form.querySelector('input[name="age_confirmed"]');
      if (ageCheckbox && !ageCheckbox.checked) {
        showError(__('Bitte bestätige dein Alter.'));
        return false;
      }
    } else {
      const birthInput = form.querySelector('input[name="birth_date"]');
      const birthDate = birthInput?.value || ctx.config?.accountData?.birth_date || '';
      const _evTz = document.querySelector('[data-event-timezone]')?.dataset?.eventTimezone || null;
      const birthResult = validateBirthDate(birthDate, minAge, _evTz);
      if (!birthResult.valid) {
        showError(birthResult.message || __('Bitte gib ein gültiges Geburtsdatum ein.'));
        return false;
      }
    }
  }

  const checkoutValidation = validateCheckoutState(form);
  if (!checkoutValidation.valid) {
    showError(checkoutValidation.message || __('Bitte prüfe die Angaben für Gruppen/Begleitpersonen.'));
    return false;
  }

  if (!ctx.isGuest) {
    const account = ctx.config?.accountData || {};
    if (!account.first_name || !account.last_name) {
      showError(__('Bitte vervollständige dein Profil unter /core/account-verwalten.'));
      return false;
    }
    if (minAge > 0 && ageMode === 'birth_date' && !account.birth_date && !form.querySelector('input[name="birth_date"]')?.value) {
      showError(__('Bitte vervollständige dein Profil (Geburtsdatum) unter /core/account-verwalten.'));
      return false;
    }
  }

  return true;
}

async function handleWalletPaymentStart(ctx) {
  if (!ctx.wallet?.paymentRequest || ctx.wallet?.busy) {
    return;
  }
  if (!validateWalletForm(ctx)) {
    return;
  }

  ctx.wallet.busy = true;

  const form = ctx.form;
  const fd = new FormData(form);
  const { payload: checkoutPayload, plusOnes } = collectCheckoutPayload(form);
  const feedback = getFeedbackBox(form);

  const inviteToken = getParamOrHidden(form, ['invite', 'invite_token']);
  const inviteCode = getParamOrHidden(form, 'invite_code');
  const accessCode = getParamOrHidden(form, 'access_code');
  const customFieldsPayload = collectCustomFields(form);
  const customFieldsJson = customFieldsPayload ? JSON.stringify(customFieldsPayload) : null;

  let botConfig = null;
  let botChallengeResponse = null;
  try {
    botConfig = await getBotProtectionConfig(ctx.config?.eventName, { inviteToken, inviteCode });
    if (botConfig?.error_code) {
      const msg = botConfig.message || __('Bot-Schutz ist fehlerhaft konfiguriert.');
      showFeedback(feedback, msg, 'error');
      showToast(msg, 'error');
      resetWalletBusy(ctx);
      return;
    }
    if (botConfig?.enabled && botConfig.mode !== 'none') {
      await injectBotProtection(form, botConfig);
      botChallengeResponse = await resolveBotProtectionResponse(botConfig, form);
      if (!botChallengeResponse) {
        const msg = __('Bitte die Bot-Verifizierung abschließen.');
        showFeedback(feedback, msg, 'error');
        showToast(msg, 'error');
        resetWalletBusy(ctx);
        return;
      }
    }
  } catch (error) {
    const msg = __('Bot-Verifizierung konnte nicht geladen werden.');
    showFeedback(feedback, msg, 'error');
    showToast(msg, 'error');
    resetWalletBusy(ctx);
    return;
  }

  const walletPayload = {
    event: ctx.config?.eventName,
    ticket_type: ctx.ticketData.ticketId,
    promo_code: checkoutPayload.promo_code || '',
    tier_id: checkoutPayload.tier_id || '',
    quantity: checkoutPayload.quantity || 1,
    group_persons: checkoutPayload.group_persons || undefined,
    invite_token: inviteToken || undefined,
    invite_code: inviteCode || undefined,
    access_code: accessCode || undefined,
    consent_dsgvo: !!fd.get('consent_dsgvo'),
    consent_agb: !!fd.get('consent_agb'),
    consent_organizer_terms: !!fd.get('consent_organizer_terms'),
    consent_marketing: !!fd.get('consent_marketing'),
    consent_event_updates: !!fd.get('consent_event_updates'),
    bot_challenge_response: botChallengeResponse || undefined,
    ...(plusOnes.length > 0 ? { plus_ones: JSON.stringify(plusOnes) } : {}),
    ...(customFieldsJson ? { custom_fields: customFieldsJson } : {}),
  };

  if (ctx.isGuest) {
    walletPayload.email = fd.get('email');
    walletPayload.first_name = fd.get('first_name');
    walletPayload.last_name = fd.get('last_name');
    walletPayload.birth_date = fd.get('birth_date') || undefined;
    walletPayload.age_confirmed = !!fd.get('age_confirmed');
    if (fd.get('provision_account')) {
      walletPayload.provision_account = true;
    }
  } else {
    const birthFromForm = fd.get('birth_date');
    if (birthFromForm) {
      walletPayload.birth_date = birthFromForm;
    }
    if (fd.get('age_confirmed')) {
      walletPayload.age_confirmed = true;
    }
  }

  const membershipRequired = form?.dataset?.membershipRequired === '1' || form?.dataset?.membershipRequired === 'true';
  const membershipNumber = membershipRequired
    ? (fd.get('membership_number') || '').trim()
    : fd.get('has_membership_number')
    ? (fd.get('membership_number') || '').trim()
    : '';
  if (membershipRequired && !membershipNumber) {
    const msg = __('Bitte gib deine Zusatzangabe an.');
    showFeedback(getFeedbackBox(form), msg, 'error');
    showToast(msg, 'error');
    resetWalletBusy(ctx);
    return;
  }
  if (membershipNumber) {
    walletPayload.membership_number = membershipNumber;
  }

  try {
    const intentResult = await apiCall(endpoints.createPaymentIntentForWallet, walletPayload);
    if (!intentResult?.success) {
      if (['account_exists', 'account_check', 'account_required'].includes(intentResult?.code) && typeof ctx.openInlineLogin === 'function') {
        showToast(
          __('Diese E-Mail ist bereits registriert. Bitte kurz einloggen und danach die Wallet-Zahlung erneut starten.'),
          'warning',
        );
        ctx.openInlineLogin(form, () => {
          showToast(__('Login erfolgreich. Bitte Apple Pay oder Google Pay erneut starten.'), 'success');
        });
        resetWalletBusy(ctx, 0);
        return;
      }
      const msg = intentResult?.toast || __('Fehler beim Erstellen der Zahlung.');
      showFeedback(feedback, msg, 'error');
      showToast(msg, 'error');
      if (botConfig?.enabled && botConfig.mode === 'simple') {
        await refreshBotProtection(form, ctx.config?.eventName, { inviteToken, inviteCode });
      }
      resetWalletBusy(ctx); // H4: 300ms debounce for error paths
      return;
    }
    const intentData = intentResult?.data || {};

    if (intentData?.free_checkout) {
      const isIdempotent = !!intentData?.idempotent;
      const toastType = isIdempotent ? 'info' : 'success';
      const msg = intentResult?.toast || __('Erfolgreich registriert!');
      showFeedback(feedback, msg, toastType);
      showToast(msg, toastType);
      resetWalletBusy(ctx, 0); // Immediate reset for success
      const redirectUrl = intentData.ticket_url || `/event/${ctx.config?.eventSlug || ''}`;
      setTimeout(() => {
        safeRedirect(redirectUrl, `/event/${ctx.config?.eventSlug || ''}`);
      }, 600);
      return;
    }

    ctx.wallet.paymentData = {
      registration: intentData.registration,
      registrationHash: intentData.registration_hash,
      paymentIntentId: intentData.payment_intent_id,
      clientSecret: intentData.client_secret,
    };

    ctx.wallet.paymentRequest.update({
      total: {
        label: intentData.product_name || ctx.ticketData.ticketName || 'Ticket',
        amount: intentData.amount_cents || getWalletAmountCents(ctx),
      },
    });

    ctx.wallet.paymentRequest.show();
  } catch (error) {
    if (botConfig?.enabled && botConfig.mode === 'simple') {
      await refreshBotProtection(form, ctx.config?.eventName, { inviteToken, inviteCode });
    }
    showToast(__('Fehler bei der Wallet-Zahlung.'), 'error');
    resetWalletBusy(ctx); // H4: 300ms debounce for error paths
  }
}

async function handleWalletPaymentMethod(ctx, ev) {
  const data = ctx.wallet?.paymentData;
  if (!data || !ctx.wallet?.stripe) {
    ev.complete('fail');
    resetWalletBusy(ctx); // H4: 300ms debounce for error paths
    return;
  }

  try {
    const { paymentIntent, error } = await ctx.wallet.stripe.confirmCardPayment(
      data.clientSecret,
      { payment_method: ev.paymentMethod.id },
      { handleActions: false },
    );

    if (error) {
      ev.complete('fail');
      // CWE-209: Don't expose Stripe error details to user
      showToast(__('Zahlung fehlgeschlagen. Bitte erneut versuchen.'), 'error');
      logger.debug('Stripe confirmCardPayment error:', error?.code || 'unknown');
      resetWalletBusy(ctx); // H4: 300ms debounce for error paths
      return;
    }

    if (paymentIntent.status === 'requires_action') {
      ev.complete('success');
      const { error: confirmError } = await ctx.wallet.stripe.confirmCardPayment(data.clientSecret);
      if (confirmError) {
        // CWE-209: Don't expose Stripe error details to user
        showToast(__('Authentifizierung fehlgeschlagen. Bitte erneut versuchen.'), 'error');
        logger.debug('Stripe 3DS confirm error:', confirmError?.code || 'unknown');
        resetWalletBusy(ctx); // H4: 300ms debounce for error paths
        return;
      }
    } else {
      ev.complete('success');
    }

    const confirmResult = await apiCall(endpoints.confirmWalletPayment, {
      registration: data.registration,
      payment_intent_id: data.paymentIntentId,
      token: data.registrationHash,
    });

    if (!confirmResult?.success) {
      showToast(confirmResult?.toast || __('Fehler bei der Zahlungsbestätigung.'), 'error');
      resetWalletBusy(ctx); // H4: 300ms debounce for error paths
      return;
    }
    const confirmData = confirmResult?.data || {};

    const successMsg = __('Zahlung erfolgreich! Dein Ticket wird generiert...');
    showFeedback(getFeedbackBox(ctx.form), successMsg, 'success');
    showToast(successMsg, 'success');

    if (confirmData.ticket_url) {
      setTimeout(() => {
        safeRedirect(confirmData.ticket_url);
      }, 1200);
    }
  } catch (error) {
    ev.complete('fail');
    showToast(__('Fehler bei der Zahlungsbestätigung.'), 'error');
  } finally {
    resetWalletBusy(ctx, 0); // Immediate reset at end of flow
  }
}

function refreshSummary(ctx) {
  const { ticketData, state } = ctx;
  const summaryEl = ctx.elements.summary;
  if (!summaryEl) {
    return;
  }

  // Lazy-init sticky summary bar on first refresh
  initStickySummary(ctx);

  // BUG-1 FIX: Sync membership number from form to state for pricing calculation
  if (ctx.form) {
    const fd = new FormData(ctx.form);
    const membershipRequired = ctx.form.dataset?.membershipRequired === '1'
      || ctx.form.dataset?.membershipRequired === 'true';
    const membershipNumber = membershipRequired
      ? (fd.get('membership_number') || '').trim()
      : fd.get('has_membership_number')
      ? (fd.get('membership_number') || '').trim()
      : '';
    state.membershipNumber = membershipNumber;
  }

  const pricing = calculatePrice(state, ticketData);
  if (pricing.selectedTier) {
    state.selectedTier = pricing.selectedTier;
  }

  const isFree = ticketData.isFree || pricing.total === 0;
  if (isFree) {
    const freeLabel = buildTicketLabel(pricing, ticketData.ticketName || 'Ticket');
    summaryEl.innerHTML = `
      <div class="text-[10px] uppercase tracking-wider checkout-helper-text mb-1.5">${__('Bestellzusammenfassung')}</div>
      <div class="flex items-center justify-between text-sm checkout-inline-line">
        <span>${escapeHtml(freeLabel)}</span>
        <span class="font-semibold">${__('Kostenlos')}</span>
      </div>
    `;
    updateSubmitButtonText(ctx.form, ticketData, pricing);
    updateStickySummary(ctx, pricing);
    updateWalletPricing(ctx);
    return;
  }

  const ticketLabel = buildTicketLabel(pricing, ticketData.ticketName || 'Ticket');

  const showUnitPrice = pricing.paidQuantity > 1 || pricing.selectedTier;
  const unitPriceInfo = showUnitPrice
    ? `<div class="text-xs checkout-helper-text mt-0.5">${formatCurrency(pricing.unitPrice, pricing.currency)} / ${__('Stück')}</div>`
    : '';

  const effectiveGroupDiscount = typeof pricing.effectiveGroupDiscount === 'number'
    ? pricing.effectiveGroupDiscount
    : pricing.groupDiscount;
  const effectiveMembershipDiscount = typeof pricing.effectiveMembershipDiscount === 'number'
    ? pricing.effectiveMembershipDiscount
    : 0;
  const effectivePromoDiscount = typeof pricing.effectivePromoDiscount === 'number'
    ? pricing.effectivePromoDiscount
    : pricing.discount;

  // Always show labeled discount lines so users see where each discount comes from
  const activeDiscountTypes = [
    effectiveGroupDiscount > 0,
    effectiveMembershipDiscount > 0,
    effectivePromoDiscount > 0,
  ].filter(Boolean).length;
  const showDiscountBreakdown = activeDiscountTypes >= 1;
  const discountLabel = buildDiscountLabel(pricing, state.promoCode);

  const capApplied = typeof pricing.discountCapApplied === 'number' ? pricing.discountCapApplied : 0;
  const maxDiscountPct = ticketData.maxCombinedDiscountPercent || 100;
  const capHint = capApplied > 0.005 && maxDiscountPct > 0 && maxDiscountPct < 100
    ? `<div class="text-xs checkout-helper-text mt-0.5">${__('Rabatt wurde auf max. {0}% begrenzt.', [escapeHtml(String(maxDiscountPct))])}</div>`
    : '';

  summaryEl.innerHTML = `
    <div class="text-[10px] uppercase tracking-wider checkout-helper-text mb-1.5">${__('Bestellzusammenfassung')}</div>
    <div class="flex items-center justify-between text-sm checkout-inline-line">
      <span>${escapeHtml(ticketLabel)}</span>
      <span>${formatCurrency(pricing.subtotal, pricing.currency)}</span>
    </div>
    ${unitPriceInfo}
    ${pricing.totalDiscount > 0 ? `
      ${showDiscountBreakdown ? `
        ${effectiveGroupDiscount > 0 ? `
        <div class="flex items-center justify-between text-xs checkout-inline-discount checkout-slide-in mt-1">
          <span>${__('Gruppenrabatt')}</span>
          <span>- ${formatCurrency(effectiveGroupDiscount, pricing.currency)}</span>
        </div>
        ` : ''}
        ${effectiveMembershipDiscount > 0 ? `
        <div class="flex items-center justify-between text-xs checkout-inline-discount checkout-slide-in mt-0.5">
          <span>${__('Mitgliederrabatt')}</span>
          <span>- ${formatCurrency(effectiveMembershipDiscount, pricing.currency)}</span>
        </div>
        ` : ''}
        ${effectivePromoDiscount > 0 ? `
        <div class="flex items-center justify-between text-xs checkout-inline-discount checkout-slide-in mt-0.5">
          <span>${escapeHtml(state.promoCode ? __('Promo-Code ({0})', [state.promoCode]) : __('Promo-Code'))}</span>
          <span>- ${formatCurrency(effectivePromoDiscount, pricing.currency)}</span>
        </div>
        ` : ''}
        ${capHint}
      ` : `
        <div class="flex items-center justify-between text-xs checkout-inline-discount checkout-slide-in mt-1">
          <span>${escapeHtml(discountLabel || __('Rabatt'))}</span>
          <span>- ${formatCurrency(pricing.totalDiscount, pricing.currency)}</span>
        </div>
        ${capHint}
      `}
    ` : ''}
    <div class="checkout-summary-divider"></div>
    <div class="flex items-center justify-between text-sm checkout-inline-total">
      <span>${__('Gesamt')}</span>
      <span>${formatCurrency(pricing.total, pricing.currency)}</span>
    </div>
  `;

  // Animate total price change with fade effect
  const totalEl = summaryEl.querySelector('.checkout-inline-total');
  if (totalEl) {
    totalEl.classList.remove('checkout-total-flash');
    void totalEl.offsetWidth; // Force reflow
    totalEl.classList.add('checkout-total-flash');
  }

  updateSubmitButtonText(ctx.form, ticketData, pricing);
  updateStickySummary(ctx, pricing);
  updateWalletPricing(ctx);
}

async function applyPromo(ctx) {
  if (ctx._promoApplying) return;

  const input = ctx.elements.promoInput;
  const feedback = ctx.elements.promoFeedback;
  if (!input || !feedback) {
    return;
  }

  const code = (input.value || '').trim().toUpperCase();
  if (!code) {
    if (ctx.state.promoCode) {
      ctx.state.promoCode = null;
      ctx.state.promoDiscount = 0;
      feedback.textContent = __('Promo-Code entfernt.');
      feedback.className = 'mt-2 text-xs checkout-promo-success';
      refreshSummary(ctx);
      return;
    }
    feedback.textContent = __('Bitte gib einen Code ein.');
    feedback.className = 'mt-2 text-xs checkout-promo-error';
    return;
  }

  ctx._promoApplying = true;
  try {
    const form = ctx.form;
    const fd = form ? new FormData(form) : null;
    const price = calculatePrice(ctx.state, ctx.ticketData);
    if (price.selectedTier) {
      ctx.state.selectedTier = price.selectedTier;
    }

    const inviteToken = getParamOrHidden(form, ['invite', 'invite_token']);
    const inviteCode = getParamOrHidden(form, 'invite_code');
    const accessCode = getParamOrHidden(form, 'access_code');
    const membershipRequired = form?.dataset?.membershipRequired === '1' || form?.dataset?.membershipRequired === 'true';
    const membershipNumber = membershipRequired
      ? (fd?.get('membership_number') || '').trim()
      : fd?.get('has_membership_number')
      ? (fd?.get('membership_number') || '').trim()
      : '';
    const emailValue = ctx.isGuest ? (fd?.get('email') || '').trim() : '';

    const result = await apiCall(endpoints.validatePromoCode, {
      event: ctx.config?.eventName,
      ticket_type: ctx.ticketData.ticketId,
      promo_code: code,
      quantity: ctx.state.quantity,
      tier_id: ctx.state.selectedTier || '',
      email: emailValue || undefined,
      membership_number: membershipNumber || undefined,
      invite_token: inviteToken || undefined,
      invite_code: inviteCode || undefined,
      access_code: accessCode || undefined,
    });

    if (result?.success && result.data?.valid !== false) {
      const data = result.data;
      ctx.state.promoCode = data.promo_code || code;
      ctx.state.promoDiscount = data.discount_amount || 0;
      feedback.textContent = __('Code eingelöst! Du sparst {0}', [formatCurrency(ctx.state.promoDiscount, price.currency)]);
      feedback.className = 'mt-2 text-xs checkout-promo-success';
    } else {
      ctx.state.promoCode = null;
      ctx.state.promoDiscount = 0;
      feedback.textContent = result?.toast || __('Ungültiger Code.');
      feedback.className = 'mt-2 text-xs checkout-promo-error';
    }
  } catch (error) {
    // H5: Sanitize error logging - only log code/message, not full object
    logger.error('Promo validation failed:', error?.code || error?.message || 'unknown');
    ctx.state.promoCode = null;
    ctx.state.promoDiscount = 0;
    feedback.textContent = __('Fehler bei der Prüfung des Codes.');
    feedback.className = 'mt-2 text-xs checkout-promo-error';
  } finally {
    ctx._promoApplying = false;
  }

  refreshSummary(ctx);
}

function schedulePromoRevalidation(ctx) {
  if (!ctx.state.promoCode) {
    return;
  }
  if (ctx.promoRevalidateTimer) {
    clearTimeout(ctx.promoRevalidateTimer);
  }
  ctx.promoRevalidateTimer = setTimeout(() => {
    ctx.promoRevalidateTimer = null;
    applyPromo(ctx).catch(() => {});
  }, PROMO_REVALIDATE_DELAY);
}

function buildEnhancements(ctx) {
  const { form, ticketData } = ctx;
  const submitButton = form.querySelector('button[type="submit"]');
  if (!submitButton) {
    return;
  }

  const container = document.createElement('div');
  container.className = 'space-y-4';
  ctx.elements.container = container;

  const infoMessages = [];
  if (infoMessages.length) {
    const infoBox = document.createElement('div');
    infoBox.innerHTML = infoMessages.map(buildInfoBox).join('');
    container.appendChild(infoBox);
  }

  if (ctx.showQuantityUi) {
    const groupWrap = document.createElement('div');
    groupWrap.innerHTML = buildGroupSection(ctx);
    container.appendChild(groupWrap.firstElementChild);
  }

  if (ctx.allowPlusOneUi) {
    const plusWrap = document.createElement('div');
    plusWrap.innerHTML = buildPlusOneSection(ctx);
    container.appendChild(plusWrap.firstElementChild);
  }

  if (ctx.allowPromoUi) {
    const promoWrap = document.createElement('div');
    promoWrap.innerHTML = buildPromoSection(ctx);
    container.appendChild(promoWrap.firstElementChild);
  }

  // Wallet section for paid tickets (Apple Pay / Google Pay)
  if (ctx.allowWalletUi) {
    const walletWrap = document.createElement('div');
    walletWrap.innerHTML = buildWalletSection();
    container.appendChild(walletWrap.firstElementChild);
  }

  const summaryWrap = document.createElement('div');
  summaryWrap.innerHTML = buildSummarySection();
  container.appendChild(summaryWrap.firstElementChild);

  // Capacity bar (remaining tickets with "Fast weg!" alert)
  const capacityHtml = buildCapacityBar(ticketData, ctx.config);
  if (capacityHtml) {
    const capacityWrap = document.createElement('div');
    capacityWrap.innerHTML = capacityHtml;
    container.appendChild(capacityWrap.firstElementChild);
  }

  // Trust signals footer for all tickets (adapted labels for free vs paid)
  {
    const trustWrap = document.createElement('div');
    trustWrap.innerHTML = buildTrustSignals(ticketData.isPaid);
    container.appendChild(trustWrap.firstElementChild);
  }

  submitButton.parentNode.insertBefore(container, submitButton);

  // Update submit button text based on ticket type
  updateSubmitButtonText(form, ticketData);

  ctx.elements.groupSection = container.querySelector('[data-checkout-group]');
  ctx.elements.groupPersons = container.querySelector('[data-group-persons]');
  ctx.elements.qtyInput = container.querySelector('[data-qty-input]');
  ctx.elements.qtyDecrease = container.querySelector('[data-qty-decrease]');
  ctx.elements.qtyIncrease = container.querySelector('[data-qty-increase]');
  ctx.elements.plusOneSection = container.querySelector('[data-checkout-plus-one]');
  ctx.elements.plusOnePersons = container.querySelector('[data-plus-one-persons]');
  ctx.elements.plusOneAdd = container.querySelector('[data-plus-one-add]');
  ctx.elements.plusOneWarning = container.querySelector('[data-plus-one-warning]');
  ctx.elements.promoInput = container.querySelector('[data-promo-input]');
  ctx.elements.promoFeedback = container.querySelector('[data-promo-feedback]');
  ctx.elements.promoApply = container.querySelector('[data-promo-apply]');
  ctx.elements.walletSection = container.querySelector('[data-checkout-wallet]');
  ctx.elements.walletApple = container.querySelector('[data-wallet-apple]');
  ctx.elements.walletGoogle = container.querySelector('[data-wallet-google]');
  ctx.elements.walletHint = container.querySelector('[data-wallet-hint]');
  ctx.elements.summary = container.querySelector('[data-checkout-summary]');
  ctx.elements.tierTable = container.querySelector('[data-tier-table]');

  if (ctx.showQuantityUi) {
    updateQuantityControls(ctx);
    if (ctx.allowGroupUi) {
      renderGroupPersons(ctx);
    }
  }
  if (ctx.allowPlusOneUi) {
    renderPlusOnes(ctx);
    updatePlusOneAvailability(ctx);
  }
  resizeContainer(form);
}

function attachListeners(ctx) {
  const { minQty, maxQty, state } = ctx;

  if (ctx.elements.qtyDecrease) {
    ctx.elements.qtyDecrease.addEventListener('click', () => {
      const newQty = clampQuantity(state.quantity - 1, minQty, maxQty);
      syncGroupPersonsFromForm(ctx.form, state);
      setQuantity(state, newQty);
      updateQuantityControls(ctx);
      renderGroupPersons(ctx);
      updatePlusOneAvailability(ctx);
      refreshSummary(ctx);
      updateTierPricingTable(ctx);
      schedulePromoRevalidation(ctx);
    });
  }

  if (ctx.elements.qtyIncrease) {
    ctx.elements.qtyIncrease.addEventListener('click', () => {
      const newQty = clampQuantity(state.quantity + 1, minQty, maxQty);
      syncGroupPersonsFromForm(ctx.form, state);
      setQuantity(state, newQty);
      updateQuantityControls(ctx);
      renderGroupPersons(ctx);
      updatePlusOneAvailability(ctx);
      refreshSummary(ctx);
      updateTierPricingTable(ctx);
      schedulePromoRevalidation(ctx);
    });
  }

  if (ctx.elements.qtyInput) {
    ctx.elements.qtyInput.addEventListener('change', () => {
      const raw = parseInt(ctx.elements.qtyInput.value, 10);
      const newQty = clampQuantity(raw, minQty, maxQty);
      syncGroupPersonsFromForm(ctx.form, state);
      setQuantity(state, newQty);
      updateQuantityControls(ctx);
      renderGroupPersons(ctx);
      updatePlusOneAvailability(ctx);
      refreshSummary(ctx);
      updateTierPricingTable(ctx);
      schedulePromoRevalidation(ctx);
    });
  }

  // Plus-One Add Button
  if (ctx.elements.plusOneAdd) {
    ctx.elements.plusOneAdd.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const addBtn = ctx.elements.plusOneAdd;
      if (addBtn.disabled) {
        return;
      }

      const maxPlus = ctx.ticketData.plusOneMax || 1;
      if (state.plusOnes.length >= maxPlus) {
        showToast(__('Vor Go-Live ist genau eine Begleitperson pro Ticket möglich.'), 'warning');
        return;
      }
      addBtn.disabled = true;
      syncPlusOnesFromForm(ctx.form, state);
      state.plusOnes.push({ first_name: '', last_name: '', email: '', birth_date: '', age_confirmed: false });
      renderPlusOnes(ctx);
      updatePlusOneControls(ctx);
      refreshSummary(ctx);
      updateTierPricingTable(ctx);
    });
  }

  if (ctx.elements.promoApply) {
    ctx.elements.promoApply.addEventListener('click', () => {
      applyPromo(ctx);
    });
  }

  if (ctx.elements.promoInput) {
    ctx.elements.promoInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        applyPromo(ctx);
      }
    });
  }

  // BUG-1 FIX: Refresh summary when membership number changes
  // so the membership discount display updates immediately.
  if (ctx.form) {
    const membershipInput = ctx.form.querySelector('[name="membership_number"]');
    if (membershipInput) {
      membershipInput.addEventListener('change', () => {
        refreshSummary(ctx);
        schedulePromoRevalidation(ctx);
      });
    }
    const membershipCheckbox = ctx.form.querySelector('[name="has_membership_number"]');
    if (membershipCheckbox) {
      membershipCheckbox.addEventListener('change', () => {
        refreshSummary(ctx);
        schedulePromoRevalidation(ctx);
      });
    }
  }
}

function validateGroupAndPlusOnes(ctx) {
  const { form, state, ticketData } = ctx;
  const options = { minAge: ticketData.minAge ?? 0, ageVerificationMode: resolveAgeMode(ticketData) };

  const validateGroupInputs = (prefix, label) => {
    let index = 0;
    while (true) {
      const firstName = form.querySelector(`[name="${prefix}first_name_${index}"]`);
      if (!firstName) {
        break;
      }
      const lastName = form.querySelector(`[name="${prefix}last_name_${index}"]`);
      const email = form.querySelector(`[name="${prefix}email_${index}"]`);
      const birthDate = form.querySelector(`[name="${prefix}birth_date_${index}"]`);
      const ageConfirmed = form.querySelector(`[name="${prefix}age_confirmed_${index}"]`);

      const fields = [firstName, lastName, email].filter(Boolean);
      for (const field of fields) {
        const result = validateField(field, options);
        if (!result.valid) {
          showFieldError(field, `${label}: ${result.message}`);
          return { valid: false, message: `${label}: ${result.message}` };
        }
        clearFieldError(field);
      }

      if (shouldShowBirthDate(ticketData) && birthDate) {
        const _evTz2 = document.querySelector('[data-event-timezone]')?.dataset?.eventTimezone || null;
        const birthResult = validateBirthDate(birthDate.value, ticketData.minAge ?? 0, _evTz2);
        if (!birthResult.valid) {
          showFieldError(birthDate, `${label}: ${birthResult.message}`);
          return { valid: false, message: `${label}: ${birthResult.message}` };
        }
        clearFieldError(birthDate);
      }

      if (shouldShowAgeCheckbox(ticketData) && ageConfirmed && !ageConfirmed.checked) {
        showFieldError(ageConfirmed, `${label}: ${__('Bitte bestätige das Alter.')}`);
        return { valid: false, message: `${label}: ${__('Bitte bestätige das Alter.')}` };
      }
      if (ageConfirmed) {
        clearFieldError(ageConfirmed);
      }

      index += 1;
    }
    return { valid: true };
  };

  if (state.groupPersons.length > 0) {
    const result = validateGroupInputs('group_', __('Gruppenmitglied'));
    if (!result.valid) {
      return result;
    }
  }

  if (state.plusOnes.length > 0) {
    const result = validateGroupInputs('plus_one_', __('Begleitperson'));
    if (!result.valid) {
      return result;
    }
  }

  // Validate required custom fields for group members and plus-ones
  const allCustomFieldDefs = ticketData.customFieldDefs || [];
  if (allCustomFieldDefs.length > 0) {
    const validatePersonCustomFields = (prefix, label, count, allowedScopes) => {
      const scopedDefs = allCustomFieldDefs.filter(f => {
        const scope = f.applies_to || 'Alle';
        return allowedScopes.includes(scope);
      });
      for (let i = 0; i < count; i++) {
        for (const field of scopedDefs) {
          if (!field.required && !field.is_required) continue;
          const key = field.name || field.field_label || field.label || '';
          if (!key) continue;
          const safeKey = key.replace(/[^a-zA-Z0-9_-]/g, '_');
          const input = form.querySelector(`[name="${prefix}cf_${safeKey}_${i}"]`);
          if (!input) continue;
          const val = (input.type === 'checkbox') ? input.checked : (input.value || '').trim();
          if (!val) {
            const displayLabel = field.label || field.field_label || __('Feld');
            showFieldError(input, `${label}: ${__('Bitte {0} ausfüllen.').replace('{0}', displayLabel)}`);
            return { valid: false, message: `${label}: ${__('Bitte {0} ausfüllen.').replace('{0}', displayLabel)}` };
          }
          clearFieldError(input);
        }
      }
      return { valid: true };
    };

    if (state.groupPersons.length > 0) {
      const cfResult = validatePersonCustomFields('group_', __('Gruppenmitglied'), state.groupPersons.length, ['Alle', 'Gruppenmitglieder']);
      if (!cfResult.valid) return cfResult;
    }
    if (state.plusOnes.length > 0) {
      const cfResult = validatePersonCustomFields('plus_one_', __('Begleitperson'), state.plusOnes.length, ['Alle', 'Plus-Ones']);
      if (!cfResult.valid) return cfResult;
    }
  }

  // GC-21 duplicate email check REMOVED — duplicate emails are allowed
  // by design for both group members and plus-ones.  Per-person ticket
  // limits (max_per_ticket / max_per_event) are enforced server-side.

  return { valid: true };
}

/**
 * Activate the expensive DOM work for a single checkout form.
 * Called lazily when the form becomes visible (expanded), not on page init.
 * This avoids ~11,000 reflows when 187+ ticket types exist on one page.
 */
export function activateEnhancements(ctx) {
  if (!ctx || ctx._activated) return;
  ctx._activated = true;
  buildEnhancements(ctx);
  attachListeners(ctx);
  refreshSummary(ctx);
  updateTierPricingTable(ctx);
}

export function initCheckoutEnhancements(root, config = {}, { openInlineLogin } = {}) {
  if (!root) {
    return;
  }

  const forms = Array.from(root.querySelectorAll('form.register-form, form.register-guest-form'));
  forms.forEach((form) => {
    if (!form || form.dataset.checkoutEnhanced === '1') {
      return;
    }

    const ticketData = extractTicketDataFromElement(form);
    ticketData.ticketName = getTicketName(form, ticketData);
    ticketData.currency = ticketData.currency || 'EUR';

    const allowGroupUi = ticketData.allowGroup;
    const allowPlusOneUi = ticketData.allowPlusOne;
    const allowPromoUi = hasPromoCodes(form) && !ticketData.isFree;
    const isGuest = form.classList.contains('register-guest-form');
    // WELLE 1 / P1-1: gate the Apple/Google Pay wallet section on the
    // `enable_wallet_payments` master switch (mirrored into walletEnabled).
    // This single guard disables BOTH the inject path (buildEnhancements →
    // ctx.allowWalletUi) and the lazy-init path (initWalletPayments trigger).
    const allowWalletUi = ticketData.walletEnabled
      && ticketData.isPaid
      && !ticketData.requiresApproval;

    const bounds = getQuantityBounds(ticketData);
    const state = createCheckoutState({ ticketData, selectedTicket: ticketData.ticketId });
    // Quantity UI only for group bookings — backend rejects qty > 1 without allow_group_booking
    const showQuantityUi = allowGroupUi;

    if (showQuantityUi) {
      setQuantity(state, bounds.minQty);
    } else {
      state.quantity = 1;
    }
    if (!allowGroupUi) {
      state.groupPersons = [];
    }

    const ctx = {
      form,
      ticketData,
      state,
      config,
      openInlineLogin,
      allowGroupUi,
      showQuantityUi,
      allowPlusOneUi,
      allowPromoUi,
      allowWalletUi,
      isGuest,
      minQty: bounds.minQty,
      maxQty: bounds.maxQty,
      elements: {},
      promoRevalidateTimer: null,
      wallet: null,
      _previousTierId: null,
      _tierAnnouncementTimeout: null,
    };

    form.dataset.checkoutEnhanced = '1';
    FORM_STATE.set(form, ctx);

    // Defer expensive DOM work (buildEnhancements, attachListeners, refreshSummary)
    // until the form is actually expanded/visible. With 187 ticket types this avoids
    // ~11,000 reflows and ~4.5 MB of closures for hidden forms.
    const isExpanded = form.dataset.expanded === '1'
      || form.closest('.ticket-checkout-inline')?.dataset.expanded === '1';
    if (isExpanded) {
      activateEnhancements(ctx);
    }

    // Lazy wallet init: defer API call until form is expanded (prevents 76x parallel requests)
    if (ctx.allowWalletUi) {
      const _tryInitWallet = () => {
        if (form.dataset.expanded === '1' && !ctx.wallet?.initialized) {
          initWalletPayments(ctx).catch((err) => {
            logger.error('Wallet initialization failed:', err?.code || err?.message || 'unknown');
          });
        }
      };

      // If form is already expanded (e.g. single-ticket events), init immediately
      if (form.dataset.expanded === '1') {
        _tryInitWallet();
      } else {
        // Watch for form expansion via data-expanded attribute
        const _walletObserver = new MutationObserver((mutations) => {
          for (const m of mutations) {
            if (m.type === 'attributes' && m.attributeName === 'data-expanded') {
              _tryInitWallet();
              _walletObserver.disconnect();
              break;
            }
          }
        });
        _walletObserver.observe(form, { attributes: true, attributeFilter: ['data-expanded'] });
      }
    }
  });
}

export function getCheckoutContext(form) {
  return FORM_STATE.get(form) || null;
}

export function syncCheckoutState(form) {
  const ctx = FORM_STATE.get(form);
  if (!ctx) {
    return null;
  }
  syncGroupPersonsFromForm(form, ctx.state);
  syncPlusOnesFromForm(form, ctx.state);
  return ctx;
}

export function validateCheckoutState(form) {
  const ctx = syncCheckoutState(form);
  if (!ctx) {
    return { valid: true };
  }
  return validateGroupAndPlusOnes(ctx);
}

export function collectCheckoutPayload(form) {
  const ctx = syncCheckoutState(form);
  if (!ctx) {
    return { payload: {}, plusOnes: [] };
  }

  const pricing = calculatePrice(ctx.state, ctx.ticketData);
  if (pricing.selectedTier) {
    ctx.state.selectedTier = pricing.selectedTier;
  }

  const payload = {
    quantity: ctx.state.quantity,
    promo_code: ctx.state.promoCode || '',
    tier_id: ctx.state.selectedTier || '',
  };

  if (ctx.state.groupPersons.length > 0) {
    payload.group_persons = JSON.stringify(ctx.state.groupPersons);
  }

  return {
    payload,
    plusOnes: ctx.state.plusOnes || [],
    ctx,
  };
}

export function resetCheckoutState(form) {
  const ctx = FORM_STATE.get(form);
  if (!ctx) {
    return;
  }
  ctx.state.groupPersons = [];
  ctx.state.plusOnes = [];
  ctx.state.promoCode = null;
  ctx.state.promoDiscount = 0;
  if (ctx.showQuantityUi) {
    setQuantity(ctx.state, ctx.minQty);
    if (ctx.allowGroupUi) {
      renderGroupPersons(ctx);
    }
    updateQuantityControls(ctx);
  }
  if (ctx.allowPlusOneUi) {
    renderPlusOnes(ctx);
    updatePlusOneAvailability(ctx);
  }
  refreshSummary(ctx);
  updateTierPricingTable(ctx);
  updateWalletPricing(ctx);
}

export async function addPlusOnes({ plusOnes = [], registration, registrationToken, isLoggedIn = false, botChallengeResponse = null } = {}) {
  if (!registration || !Array.isArray(plusOnes) || plusOnes.length === 0) {
    return { success: true };
  }

  const created = [];
  let plusOneError = null;

  for (const plusOne of plusOnes) {
    try {
      const poResult = await apiCall(endpoints.addPlusOne, {
        registration,
        first_name: plusOne.first_name,
        last_name: plusOne.last_name,
        email: plusOne.email,
        birth_date: plusOne.birth_date || null,
        age_confirmed: plusOne.age_confirmed || false,
        token: registrationToken || undefined,
        bot_challenge_response: botChallengeResponse || undefined,
        membership_number: plusOne.membership_number || undefined,
        custom_fields: plusOne.custom_fields ? JSON.stringify(plusOne.custom_fields) : undefined,
      });

      if (poResult?.success && poResult?.data?.plus_one_registration) {
        created.push({
          id: poResult.data.plus_one_registration,
          token: poResult.data.registration_hash || poResult.data.registrationHash || null,
        });
      } else {
        plusOneError = poResult?.toast || __('Begleitperson konnte nicht hinzugefügt werden.');
        break;
      }
    } catch (err) {
      plusOneError = __('Netzwerkfehler beim Hinzufügen der Begleitperson.');
      // H5: Sanitize error logging
      logger.error('Plus-One error:', err?.code || err?.message || 'unknown');
      break;
    }
  }

  if (!plusOneError) {
    return { success: true };
  }

  if (created.length > 0) {
    for (const item of created) {
      try {
        if (item?.id && item?.token) {
          await apiCall(`${endpoints.registerGuestForTicket.replace('register_guest_for_ticket', 'cancel_registration_by_token')}`, {
            registration: item.id,
            token: item.token,
            silent: 1,
          });
        } else if (item?.id && isLoggedIn) {
          await apiCall(endpoints.cancelRegistration, { registration: item.id });
        }
      } catch (rollbackErr) {
        logger.error('Plus-One rollback failed', rollbackErr);
      }
    }
  }

  return { success: false, error: plusOneError };
}
